/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { ProductPrice } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
import { getCartState } from '@msdyn365-commerce/global-state';
import { getCountryLanguageAsync } from '../../../../actions/DataActionExtension.g';
import * as React from 'react';

const defaultPrice: number = 0;
const freePrice: number = 0;

// Simple country data storage with sessionStorage
const COUNTRY_DATA_KEY = 'country_data';
const CURRENCY_DECIMAL_KEY = 'currency_decimal';

// Storage utility functions
const getStoredCountryData = () => {
    try {
        const stored = sessionStorage.getItem(COUNTRY_DATA_KEY);
        return stored ? JSON.parse(stored) : null;
    } catch (error) {
        return null;
    }
};

const setStoredCountryData = (countryData: any) => {
    try {
        sessionStorage.setItem(COUNTRY_DATA_KEY, JSON.stringify(countryData));
    } catch (error) {
        console.log('Error storing country data:', error);
    }
};

const getStoredCurrencyDecimal = () => {
    try {
        const stored = sessionStorage.getItem(CURRENCY_DECIMAL_KEY);
        return stored ? parseFloat(stored) : null;
    } catch (error) {
        console.log('Error reading stored currency decimal:', error);
        return null;
    }
};

const setStoredCurrencyDecimal = (decimal: number) => {
    try {
        sessionStorage.setItem(CURRENCY_DECIMAL_KEY, decimal.toString());
    } catch (error) {
        console.log('Error storing currency decimal:', error);
    }
};

// // Debug function to log cache status
// const logCacheStatus = () => {
//     console.log(`Currency cache size: ${currencyCache.size}`);
//     for (const [key, value] of currencyCache.entries()) {
//         const age = Date.now() - value.timestamp;
//         console.log(`Cache entry: ${key}, age: ${Math.round(age / 1000)}s`);
//     }
// };

/**
 * Interface for price component resources.
 * @param {string} priceRangeSeparator - The price range separator.
 */
export interface IPriceComponentResources {
    priceRangeSeparator?: string;
}

/**
 * Interface for price component props.
 * @param {boolean} isPriceMinMaxEnabled - Whether the price range feature is enabled or not.
 * @param {IPriceComponentResources} priceResources - The price range resources.
 */
export interface IPriceComponentProps extends IComponentProps<{ price: ProductPrice }> {
    className?: string;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    isPriceMinMaxEnabled?: boolean;
    priceResources?: IPriceComponentResources;
    localCurrency?: boolean;
    countryRegion?: string;
    customMaxAmount?: number | null;
    customMinAmount?: number | null;
    customSetAmount?: number | null;
    isReady?: boolean;
}
export interface IPriceComponent extends IComponent<IPriceComponentProps> {
    localCurrency?: boolean;
    countryRegion?: string;
    customMaxAmount?: number | null;
    customMinAmount?: number | null;
    customSetAmount?: number | null;
}

const priceComponentActions = {};

/**
 * Price.
 * @param props - The props.
 * @returns The JSX.Element.
 */
const Price: React.FC<IPriceComponentProps> = (props: IPriceComponentProps): JSX.Element | null => {
    const [cart, setCart] = React.useState<any>();
    const [countries, setCountries] = React.useState<any>();
    const [currency, setCurrency] = React.useState<any>();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [currentCountry, setCurrentCountry] = React.useState<string>('');
    const [countryData, setCountryData] = React.useState<any>(null);

    const getCart = async () => {
        const cart = await getCartState(props.context.actionContext);
        setCart(cart);
        props.isReady = true;
        return cart;
    };

    // Extract current country from cart
    const extractCurrentCountry = (cartData: any): string => {
        if (cartData && cartData.cart && cartData.cart.ExtensionProperties) {
            const countryProperty = cartData.cart.ExtensionProperties?.find((x: any) => x.Key === 'Country');
            return countryProperty?.Value?.StringValue || '';
        }
        return '';
    };

    const getCurrency = async (country: any) => {
        if (!country) {
            return null;
        }

        // Check if we have stored country data for this country
        const storedCountryData = getStoredCountryData();

        if (storedCountryData && storedCountryData.countryRegionId === country) {
            setCountries([storedCountryData]); // Wrap in array to match expected format
            return [storedCountryData];
        }

        // Only call API if we don't have data for this country or not already loading
        if (!isLoading) {
            setIsLoading(true);
            try {
                const result = await getCountryLanguageAsync(
                    { callerContext: props.context.actionContext },
                    props.context.actionContext.requestContext.channel!.RecordId,
                    country || 'en-us'
                );

                if (result && result.length > 0) {
                    // Find the specific country data
                    const countryData = result.find((item: any) => item.countryRegionId === country);

                    if (countryData) {
                        // Store only the specific country data
                        setStoredCountryData(countryData);
                        setCountries([countryData]);
                    } else {
                        setCountries(result);
                    }
                }

                setIsLoading(false);
                return result;
            } catch (error) {
                console.log('Error fetching currency data:', error);
                setIsLoading(false);
                return null;
            }
        }

        return countries; // Return existing data if already loading
    };

    let storageCheck;
    // let countryCheck;

    // Filter country data based on current country
    React.useEffect(() => {
        if (countries && countries[0] && currentCountry) {
            const filteredData = countries.filter((country: any) => {
                return country.countryRegionId === currentCountry;
            });
            setCountryData(filteredData);
        } else {
            setCountryData(null);
        }
    }, [countries, currentCountry]);

    // Get currency decimal from stored data or calculate from current country data
    let currencyDecimal: any;
    const storedCurrencyDecimal = getStoredCurrencyDecimal();

    if (storedCurrencyDecimal) {
        currencyDecimal = storedCurrencyDecimal;
    } else if (countryData && countryData[0]) {
        currencyDecimal =
            //@ts-ignore
            countryData[0].ExtensionProperties[7].Value?.StringValue === countryData[0].ExtensionProperties[8].Value?.StringValue
                ? undefined
                : //@ts-ignore
                  countryData[0].ExtensionProperties[6].Value?.DecimalValue / 100;

        if (currencyDecimal) {
            setStoredCurrencyDecimal(currencyDecimal);
        }
    }

    // if (this.state.isCustomPriceSelected && newPrice) {
    //     newPrice.CustomerContextualPrice = customPrice;
    // }
    let shouldShowLocalCurrency = false;
    let currentCountryRegion =
        countryData && countryData[0] && countryData[0].ExtensionProperties && countryData[0].ExtensionProperties[8].Value?.StringValue
            ? countryData[0].ExtensionProperties[8].Value?.StringValue
            : null;
    let currencyDecimalCheck;
    // Update current country when cart changes
    React.useEffect(() => {
        if (cart) {
            const extractedCountry = extractCurrentCountry(cart);
            if (extractedCountry !== currentCountry) {
                setCurrentCountry(extractedCountry);
            }
        }
    }, [cart]);

    React.useEffect(() => {
        // Only run when we have a valid currentCountry
        if (currentCountry && currentCountry !== '') {
            if (sessionStorage) {
                sessionStorage.setItem('country', currentCountry);
            }

            // Get currency data with caching
            getCurrency(currentCountry);
        }
    }, [currentCountry]); // Only depend on currentCountry changes

    // Get cart data on component mount
    React.useEffect(() => {
        getCart();
    }, []);

    React.useEffect(() => {
        // Handle currency decimal storage separately
        if (currencyDecimal) {
            setStoredCurrencyDecimal(currencyDecimal);
        }

        currencyDecimalCheck = getStoredCurrencyDecimal();
        storageCheck = getStoredCurrencyDecimal()?.toString();
        setCurrency(storageCheck);
    }, [currencyDecimal]);
    if (
        (currencyDecimal || currencyDecimalCheck) &&
        props.context.request.telemetryPageName !== 'CartPage' &&
        props.context.request.telemetryPageName !== 'CheckoutPage' &&
        props.context.request.telemetryPageName !== 'orderconfirmation' &&
        props.context.request.telemetryPageName !== 'OrderDetails'
    ) {
        shouldShowLocalCurrency = true;
    } else {
        currentCountryRegion =
            countryData && countryData[0] && countryData[0].ExtensionProperties && countryData[0].ExtensionProperties[8].Value?.StringValue
                ? countryData[0].ExtensionProperties[7].Value?.StringValue
                : null;
    }
    if (currency) {
        props.countryRegion = currency;
    }
    if (currentCountryRegion) {
        if (storageCheck !== currentCountryRegion) {
            sessionStorage.setItem('currencyDecimal', currentCountryRegion);
        }
        props.countryRegion = storageCheck ? storageCheck : currentCountryRegion;
        let maxPriceCopy = props.data.price.MaxVariantPrice || 0;
        const customMaxAmount = props.data.price.MaxVariantPrice && currencyDecimal ? (maxPriceCopy *= currencyDecimal) : null;
        let minPriceCopy = props.data.price.MinVariantPrice || 0;
        const customMinAmount = props.data.price.MinVariantPrice && currencyDecimal ? (minPriceCopy *= currencyDecimal) : null;
        let adjustedPriceCopy = props.data.price.AdjustedPrice || 0;
        const customSetAmount = props.data.price.AdjustedPrice && currencyDecimal ? (adjustedPriceCopy *= currencyDecimal) : null;
        props.customMaxAmount = customMaxAmount;
        props.customMinAmount = customMinAmount;
        props.customSetAmount = customSetAmount;
        props.localCurrency = shouldShowLocalCurrency;
        props.isPriceMinMaxEnabled = true;
        props.isReady = true;
    }
    // CustomerContextualPrice could be zero
    if (props.data.price.CustomerContextualPrice === undefined) {
        return null;
    }

    return (
        <span className={classnames('msc-price', props.className)}>
            {/* {showStrikethroughPricing(props) ? renderCurrentPriceWithOriginalPrice(props) : renderCurrentPrice(props)} */}
            {renderCurrentPrice(props)}
        </span>
    );
};

/**
 * ShowStrikethroughPricing.
 * @param props - The props.
 * @returns True if showStrikethroughPricing, false otherwise.
 */
// @ts-ignore
// const showStrikethroughPricing = (props: IPriceComponentProps): boolean => {
//     const originalPrice = getOriginalPrice(props);
//     if (
//         props.isPriceMinMaxEnabled &&
//         props.data.price.MaxVariantPrice &&
//         props.data.price.MinVariantPrice &&
//         props.data.price.MaxVariantPrice > props.data.price.MinVariantPrice
//     ) {
//         return props.data.price.MinVariantPrice < originalPrice;
//     }

//     if (originalPrice && props.data.price.CustomerContextualPrice) {
//         return originalPrice > props.data.price.CustomerContextualPrice;
//     }

//     return false;
// };

const getOriginalPrice = (props: IPriceComponentProps): number => {
    return Math.max(
        props.data.price.BasePrice ?? defaultPrice,
        props.data.price.TradeAgreementPrice ?? defaultPrice,
        props.data.price.AdjustedPrice ?? defaultPrice
    );
};

const formatCurrency = (props: IPriceComponentProps, price: number | undefined, shouldUseFreePriceText: boolean | undefined): string => {
    if (price === undefined) {
        return '';
    }

    if (shouldUseFreePriceText && price === freePrice && props.freePriceText) {
        return '-.--';
    }
    return props.context.cultureFormatter.formatCurrency(price);
};

const renderCurrentPrice = (props: IPriceComponentProps): JSX.Element => {
    const initialPrice: string = formatCurrency(props, props.data.price.CustomerContextualPrice, true);
    const maxVariantPrice: string = formatCurrency(props, props.data.price.MaxVariantPrice, false);
    const minVariantPrice: string = formatCurrency(props, props.data.price.MinVariantPrice, false);

    let containsCurrencyLettersCheck: any;
    let containsCurrencySymbolsCheck: any;

    let currencyType: any = '';

    const currencySymbols = [
        '$',
        '€',
        '£',
        '¥',
        '₣',
        '₹',
        'د.ك',
        'د.إ',
        '﷼‎',
        '₻',
        '₽',
        '₾',
        '₺',
        '₼',
        '₸',
        '₴',
        '₷',
        '฿',
        '원',
        '₫',
        '₮',
        '₯',
        '₱',
        '₳',
        '₵',
        '₲',
        '₪',
        '₰'
    ];
    const containsCurrencySymbol = (inputString: any) => {
        for (const char of inputString) {
            if (currencySymbols.includes(char)) {
                return true;
            }
        }

        return false;
    };

    function containsCurrencyLetters(inputString: any) {
        const regexPattern = new RegExp(`[A-Za-z]`);
        return regexPattern.test(inputString);
    }
    if (
        props.isPriceMinMaxEnabled &&
        props.data.price.MaxVariantPrice &&
        props.data.price.MinVariantPrice &&
        props.data.price.MaxVariantPrice > props.data.price.MinVariantPrice
    ) {
        let newMinPrice;
        let newMaxPrice;
        if (props.countryRegion) {
            if (minVariantPrice.includes(props.countryRegion)) {
                newMinPrice = minVariantPrice.includes(props.countryRegion.slice(0, -1))
                    ? minVariantPrice.replace(props.countryRegion, '')
                    : minVariantPrice;
                newMaxPrice = maxVariantPrice.includes(props.countryRegion.slice(0, -1))
                    ? maxVariantPrice.replace(props.countryRegion, '')
                    : maxVariantPrice;
            } else {
                newMinPrice = minVariantPrice.includes(props.countryRegion.slice(0, -1))
                    ? minVariantPrice.replace(props.countryRegion.slice(0, -1), '')
                    : minVariantPrice;
                newMaxPrice = maxVariantPrice.includes(props.countryRegion.slice(0, -1))
                    ? maxVariantPrice.replace(props.countryRegion.slice(0, -1), '')
                    : maxVariantPrice;
            }
        }
        if (newMinPrice) {
            // containsCurrencyLettersCheck = containsCurrencySymbol(newMinPrice);
            containsCurrencyLettersCheck = containsCurrencyLetters(newMinPrice);
            containsCurrencySymbolsCheck = containsCurrencySymbol(newMinPrice);
        } else if (newMaxPrice) {
            // containsCurrencyLettersCheck = containsCurrencySymbol(newMaxPrice);
            containsCurrencyLettersCheck = containsCurrencyLetters(newMaxPrice);
            containsCurrencySymbolsCheck = containsCurrencySymbol(newMaxPrice);
        }

        if (containsCurrencyLettersCheck === false && containsCurrencySymbolsCheck === true && props.countryRegion) {
            currencyType = props.countryRegion;
        } else if (containsCurrencyLettersCheck === false && containsCurrencySymbolsCheck === false && props.countryRegion) {
            currencyType = props.countryRegion;
        }
        return (
            <span className='msc-price__pricerange' itemProp='price'>
                {' '}
                <span className='msc-price__minprice' itemProp='price'>
                    {!props.localCurrency && newMinPrice && `${newMinPrice} ${currencyType}`}
                    {props.localCurrency &&
                        props.isReady &&
                        props.customMinAmount &&
                        `${props.customMinAmount.toFixed(2)} ${props.countryRegion ? props.countryRegion : ''}`}
                    {props.localCurrency &&
                        !props.customMinAmount &&
                        `${props.data.price.MinVariantPrice.toFixed(2)} ${props.countryRegion ? props.countryRegion : ''}`}
                </span>
                <span className='msc-price__separator' itemProp='price'>
                    {props.isReady && props.priceResources?.priceRangeSeparator}
                </span>
                <span className='msc-price__maxprice' itemProp='price'>
                    {!props.localCurrency && props.isReady && newMaxPrice && `${newMaxPrice} ${currencyType}`}
                    {props.localCurrency &&
                        props.isReady &&
                        props.customMaxAmount &&
                        `${props.customMaxAmount.toFixed(2)} ${props.countryRegion ? props.countryRegion : ''}`}
                    {props.localCurrency &&
                        !props.customMaxAmount &&
                        `${props.data.price.MaxVariantPrice.toFixed(2)} ${props.countryRegion ? props.countryRegion : ''}`}
                </span>{' '}
            </span>
        );
    }
    let newInitialPrice;
    if (props.countryRegion) {
        if (initialPrice.includes(props.countryRegion)) {
            newInitialPrice = initialPrice.includes(props.countryRegion.slice(0, -1))
                ? initialPrice.replace(props.countryRegion, '')
                : initialPrice;
        } else {
            newInitialPrice = initialPrice.includes(props.countryRegion.slice(0, -1))
                ? initialPrice.replace(props.countryRegion.slice(0, -1), '')
                : initialPrice;
        }
    }
    if (initialPrice) {
        // containsCurrencyLettersCheck = containsCurrencySymbol(initialPrice);
        containsCurrencyLettersCheck = containsCurrencyLetters(newInitialPrice);
        containsCurrencySymbolsCheck = containsCurrencySymbol(initialPrice);
    }

    if (containsCurrencyLettersCheck === false && containsCurrencySymbolsCheck === true && props.countryRegion) {
        currencyType = props.countryRegion;
    } else if (containsCurrencyLettersCheck === false && containsCurrencySymbolsCheck === false && props.countryRegion) {
        currencyType = props.countryRegion;
    }

    return (
        <span className='msc-price__actual' itemProp='price'>
            {!props.localCurrency && newInitialPrice && `${newInitialPrice} ${currencyType}`}
            {props.localCurrency &&
                props.isReady &&
                props.customSetAmount &&
                `${props.customSetAmount.toFixed(2)} ${props.countryRegion ? props.countryRegion : ''}`}
            {props.localCurrency &&
                !props.customSetAmount &&
                props.data.price.CustomerContextualPrice &&
                `${props.data.price.CustomerContextualPrice.toFixed(2)} ${props.countryRegion ? props.countryRegion : ''}`}
        </span>
    );
};
// @ts-ignore
// const renderCurrentPriceWithOriginalPrice = (props: IPriceComponentProps): JSX.Element => {
//     const originalPrice: number = getOriginalPrice(props);
//     const initialPrice: string = formatCurrency(props, originalPrice, true);

//     return (
//         <>
//             <span className='sr-only'>
//                 {' '}
//                 {props.originalPriceText} {initialPrice} {props.currentPriceText} {renderCurrentPrice(props)}
//             </span>
//             <span className='msc-price__strikethrough' aria-hidden='true'>
//                 {initialPrice}
//             </span>
//             <span aria-hidden='true'>{renderCurrentPrice(props)}</span>
//             {props.savingsText && <span className='msc-price__savings'>{props.savingsText}</span>}
//         </>
//     );
// };

export const PriceComponent: React.FunctionComponent<IPriceComponentProps> = msdyn365Commerce.createComponentOverride<IPriceComponent>(
    'Price',
    { component: Price, ...priceComponentActions }
);

export default PriceComponent;
